import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Send, X, Download, Calendar, Target, Users, BarChart3, Handshake, Award, Zap } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/b9ee4d00-e4fb-4346-bcce-3198c4f31590/images/9b4af3e34c00dc8fef5d61567a74a9495fa217158ad395aeec36c9386f85abed.jpeg',
  journey: 'https://static.prod-images.emergentagent.com/jobs/f003bd6d-97d3-4506-aab8-467d347e5410/images/4b40ef614ce543644f03db11a3d7bbb0cfcbfdeb2f63150389aecc34c8dee552.jpeg',
  anatomy: 'https://static.prod-images.emergentagent.com/jobs/b9ee4d00-e4fb-4346-bcce-3198c4f31590/images/edbd1df27aefb801e0746db2d82f21407e7b4ae0459e7be441cb68178292397a.jpeg',
  clubs: 'https://static.prod-images.emergentagent.com/jobs/b9ee4d00-e4fb-4346-bcce-3198c4f31590/images/06198616692e44badb1474d506610e16bf92bf6a34d22c84fd053383027897d6.jpeg',
  strategy: 'https://static.prod-images.emergentagent.com/jobs/b9ee4d00-e4fb-4346-bcce-3198c4f31590/images/400908ab98ced6cd2703d6a82ef279391a5dd618a9cafe860fc06dd116881f6d.jpeg',
  practice: 'https://images.unsplash.com/photo-1624456864551-257e5a864f8d?w=800&q=80',
  networking: 'https://static.prod-images.emergentagent.com/jobs/b9ee4d00-e4fb-4346-bcce-3198c4f31590/images/dfbd8f63475ea521d6f06273b302d38cc9bfda50c90c6537b29c093c46298aca.jpeg',
};

const SESSIONS = [
  {
    num: '01', title: 'The Foundation — Why Golf for Business',
    desc: 'Understand why golf is the #1 sport for executive networking and how it mirrors strategic business thinking.',
    points: ['The boardroom-to-fairway connection', 'How top CEOs use golf for deal-making', 'Etiquette that builds trust & credibility', 'First swing fundamentals'],
    img: IMG.hero, icon: Award,
  },
  {
    num: '02', title: 'Course Anatomy & Equipment Mastery',
    desc: 'Learn the course layout, understand every club in the bag, and develop the technical vocabulary that earns respect on the course.',
    points: ['Par 4 hole anatomy: tee, fairway, green, hazards', 'Club selection strategy: driver to putter', 'Grip, stance & swing basics', 'Reading the course like a P&L statement'],
    img: IMG.anatomy, icon: Target,
  },
  {
    num: '03', title: 'Strategy, Precision & Sales Parallels',
    desc: 'Draw direct parallels between golf strategy and enterprise sales execution — from analysing the lie to closing the deal.',
    points: ['Analyse Lie → Account Planning', 'Club Selection → Sales Approach', 'Wind Factor → Market Conditions', 'Target Zone → Deal Closure'],
    img: IMG.strategy, icon: BarChart3,
  },
  {
    num: '04', title: 'On-Course Real Practice Sessions',
    desc: 'Put theory into practice at Bengaluru\'s best golf courses. We have tie-ups with top courses and coaches to give you hands-on experience.',
    points: ['Range sessions with the best golf coaches and players', 'Understand each club and its real-world usage', 'Plan your training with peers — build regular team bonding sessions', 'Each session doubles as a networking event'],
    img: IMG.practice, icon: Zap,
  },
  {
    num: '05', title: 'Networking, Deals & the 19th Hole',
    desc: 'Master the art of relationship-building on the course. Learn how to turn a round of golf into a pipeline of opportunities.',
    points: ['Conversation flow during 18 holes', 'When to talk business vs. when to play', 'The 19th hole: closing over drinks', 'Building a golf networking calendar'],
    img: IMG.networking, icon: Handshake,
  },
];

const BENEFITS = [
  'Build relationships with C-suite executives in a relaxed setting',
  'Develop a new professional networking channel',
  'Learn a skill that opens doors for decades',
  'Parallel golf strategy with business strategy thinking',
  'Access exclusive corporate golf events & invitations',
  'Stand out in client entertainment & hospitality situations',
];

const inputClass = "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all";

const BrochureModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="brochure-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 z-10">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="modal-close"><X className="w-5 h-5" /></button>
        <div className="text-center mb-6">
          <Download className="w-10 h-10 text-accent mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-foreground mb-2">Download Brochure</h3>
          <p className="text-muted-foreground text-sm">Get the full program details for Golf for Corporate Success.</p>
        </div>
        <div className="space-y-4">
          <a href="/downloads/AvantGarde-Golf101-Brochure.html" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-accent hover:bg-accent/5 transition-all group">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground group-hover:text-accent transition-colors">Golf for Corporate Success — Full Brochure</p>
              <p className="text-xs text-muted-foreground">2-page programme overview with sessions, phases & sign-up</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

const LeadForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', service_interest: 'corporate-golf', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      await axios.post(`${API}/api/leads`, { ...form, source: 'corporate-golf' });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-6 text-center py-12" data-testid="golf-lead-success">
        <CheckCircle className="w-16 h-16 text-accent" />
        <h3 className="text-2xl font-bold text-foreground">Thank You!</h3>
        <p className="text-muted-foreground max-w-md">We'll reach out within 24 hours to discuss the program and schedule your first session.</p>
        <button onClick={() => setSubmitted(false)} className="rounded-lg border border-border px-6 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors">Submit Another Request</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="golf-lead-form">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="golf-lead-error">
          Something went wrong. Please try again or contact us directly at sameerpant@avant-gardeconsulting.co.in
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
          <input type="text" name="name" value={form.name} onChange={handleChange} required className={inputClass} placeholder="Your name" data-testid="golf-input-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} placeholder="you@company.com" data-testid="golf-input-email" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
          <input type="tel" name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="+91 98765 43210" data-testid="golf-input-phone" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Company</label>
          <input type="text" name="company" value={form.company} onChange={handleChange} className={inputClass} placeholder="Your Company" data-testid="golf-input-company" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">What interests you most?</label>
        <select name="service_interest" value={form.service_interest} onChange={handleChange} className={inputClass} data-testid="golf-input-interest">
          <option value="corporate-golf">Full 5-Session Program</option>
          <option value="golf-networking">Networking & Deal-Making Session</option>
          <option value="golf-team-building">Corporate Team Building Event</option>
          <option value="golf-private">Private Executive Coaching</option>
          <option value="golf-other">Other / Custom Program</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Message (Optional)</label>
        <textarea name="message" value={form.message} onChange={handleChange} rows={3} className={`${inputClass} resize-y`} placeholder="Tell us about your goals..." data-testid="golf-input-message" />
      </div>
      <button type="submit" disabled={loading} data-testid="golf-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-base px-6 py-3.5 hover:bg-accent/90 disabled:opacity-50 transition-all">
        {loading ? 'Sending...' : 'Request Program Details'} <Send className="w-4 h-4" />
      </button>
    </form>
  );
};

export default function CorporateGolfPage() {
  const [brochureOpen, setBrochureOpen] = useState(false);

  const scrollToForm = useCallback(() => {
    document.getElementById('golf-consultation')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div>
      <BrochureModal open={brochureOpen} onClose={() => setBrochureOpen(false)} />

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="golf-hero">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Golf for Corporate Success" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <p className="text-xs font-bold uppercase tracking-widest text-accent mb-4">Avant-Garde Consulting</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 max-w-2xl" data-testid="golf-title">
            Golf for Corporate Success
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-xl leading-relaxed mb-8">
            A 5-session executive program that transforms non-golfers into confident networkers on the course. Where business strategy meets the fairway.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={scrollToForm} data-testid="golf-hero-cta"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-8 py-4 hover:bg-accent/90 hover-lift transition-all group">
              Enrol Now <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setBrochureOpen(true)} data-testid="golf-brochure-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/15 border border-white/25 text-white font-semibold text-lg px-8 py-4 hover:bg-white/25 transition-all">
              <Download className="w-5 h-5" /> Download Brochure
            </button>
          </div>
        </div>
      </section>

      {/* Why Golf */}
      <section className="py-20 bg-background px-4 sm:px-6 lg:px-8" data-testid="golf-why">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Why Golf Is the Ultimate Business Skill</h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            More deals are closed on the golf course than in boardrooms. Yet most professionals never learn the game. This program bridges that gap — teaching you not just how to play, but how to leverage golf as a powerful tool for networking, relationship-building, and closing deals.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Whether you're a founder, CXO, or sales leader, the ability to confidently step onto a golf course opens doors that no LinkedIn message ever could.
          </p>
        </div>
      </section>

      {/* 4-Phase Journey */}
      <section className="py-20 bg-muted px-4 sm:px-6 lg:px-8" data-testid="golf-journey">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-4">The 5-Session Journey</h2>
          <p className="text-muted-foreground text-center mb-6 text-lg">From first grip to first deal — a structured path to golf confidence.</p>
          <div className="mb-12">
            <img src={IMG.journey} alt="4-Phase Program Journey" className="w-full max-w-4xl mx-auto rounded-2xl shadow-lg" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SESSIONS.map((session) => {
              const Icon = session.icon;
              return (
                <div key={session.num} className="card-depth rounded-2xl overflow-hidden" data-testid={`golf-session-${session.num}`}>
                  <img src={session.img} alt={session.title} className="w-full h-48 object-cover" />
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-accent font-bold text-lg">{session.num}</span>
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">{session.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{session.desc}</p>
                    <ul className="space-y-2">
                      {session.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equipment Preview */}
      <section className="py-16 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-10">Know Your Equipment</h2>
          <img src={IMG.clubs} alt="Golf Club Line-up" className="w-full rounded-2xl shadow-lg" />
          <p className="text-muted-foreground text-center mt-6 text-sm max-w-2xl mx-auto">
            From driver to putter — learn which club to use and when, just like choosing the right sales approach for each client.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-primary/5 px-4 sm:px-6 lg:px-8" data-testid="golf-benefits">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-12">What You'll Gain</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm border border-border">
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-foreground font-medium text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-20 hero-gradient px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">Who Is This Program For?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Users, title: 'Founders & CEOs', desc: 'Looking to network with investors, partners, and enterprise clients in premium settings.' },
              { icon: BarChart3, title: 'Sales & BD Leaders', desc: 'Who want a competitive edge in client entertainment and relationship-building.' },
              { icon: Zap, title: 'Rising Executives', desc: 'Preparing for C-suite roles where golf is part of the professional culture.' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/10 border border-white/20 rounded-2xl p-6 text-left">
                  <Icon className="w-8 h-8 text-accent mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="golf-consultation" className="py-20 lg:py-28 bg-muted px-4 sm:px-6 lg:px-8" data-testid="golf-consultation">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Calendar className="w-10 h-10 text-accent mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Get Started</h2>
            <p className="text-muted-foreground text-lg">Interested in the program? Fill in your details and we'll reach out within 24 hours.</p>
          </div>
          <div className="card-depth rounded-2xl p-8 lg:p-10">
            <LeadForm />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 cta-gradient animate-gradient" />
        <div className="absolute inset-0 hero-texture opacity-50" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Your next deal might start on the first tee.</h2>
          <p className="text-lg text-white/75 mb-8">Join the program that's changing how professionals think about networking.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group">
              Enrol Now <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setBrochureOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/15 border border-white/25 text-white font-semibold text-lg px-8 py-4 hover:bg-white/25 transition-all">
              <Download className="w-5 h-5" /> Download Brochure
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
